import React, { useEffect, useRef } from 'react'
import { View, StyleSheet } from 'react-native'
import { Button, Text, Modal, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { roundQuantity } from '../utils/stockShortage'

/**
 * ADS-FIX-2026-003 — what a `stock_unavailable` refusal shows: which product is short, how many are
 * in stock, how many were attempted, and (for a role that may receive goods) one button into the
 * ordinary acceptance form prefilled with those products.
 *
 * It is a description and a door, never an action: the sale stays refused, and the button only
 * navigates. Stock moves only if the cashier then saves the acceptance in that form.
 *
 * @param shortages   StockShortage[] (utils/stockShortage.ts); the modal is hidden when empty
 * @param canReceive  whether to offer the acceptance path (the role's `Supplies` permission)
 * @param isReceivable (shortage) => whether that product can be received (still in the catalog)
 * @param onReceive   (shortage) => open the acceptance form for that ONE product
 * @param onClose     dismiss — the cart is untouched either way
 * @param onShown     ({ receivingOffered }) => void — called once each time the dialog BECOMES
 *                    visible (ADS-INSTR-2026-003: the proof that this device runs the guidance).
 *                    Not again on a re-render, nor when a row is added while it is already open.
 */
export function StockShortageModal({ shortages, canReceive, isReceivable, onReceive, onClose, onShown }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const visible = Array.isArray(shortages) && shortages.length > 0
  // The «record an acceptance» hint only makes sense when at least one row offers the button.
  const offersReceiving = Boolean(canReceive && onReceive && isReceivable && (shortages || []).some(isReceivable))
  // Read through refs so the effect below depends on `visible` alone: a new callback identity or
  // another row arriving while the dialog is open is the same display, not a second one.
  const onShownRef = useRef(onShown)
  onShownRef.current = onShown
  const offersReceivingRef = useRef(offersReceiving)
  offersReceivingRef.current = offersReceiving
  useEffect(() => {
    if (visible) onShownRef.current?.({ receivingOffered: offersReceivingRef.current })
  }, [visible])
  const format = (value) => (typeof value === 'number' && Number.isFinite(value) ? String(roundQuantity(value)) : '—')

  return (
    <Modal
      style={[styles.modal, { backgroundColor: theme?.['background-basic-color-1'] }]}
      visible={visible}
      backdropStyle={styles.backdrop}
      onBackdropPress={onClose}
      testID="stock-shortage-modal"
    >
      <Text category="h6" style={styles.title}>{t('newSale.stockShortageTitle')}</Text>
      {(shortages || []).map((shortage, index) => (
        <View key={shortage.productId || `line-${index}`} style={styles.row} testID="stock-shortage-row">
          <Text category="s1">{shortage.productName || '—'}</Text>
          {shortage.ingredientOf ? (
            <Text appearance="hint" category="c1">
              {t('newSale.stockShortageIngredientOf', { productName: shortage.ingredientOf })}
            </Text>
          ) : null}
          <Text testID="stock-shortage-available">
            {t('newSale.stockShortageAvailable', { quantity: format(shortage.available) })}
          </Text>
          <Text testID="stock-shortage-attempted">
            {t('newSale.stockShortageAttempted', { quantity: format(shortage.attempted) })}
          </Text>
          {canReceive && onReceive && isReceivable?.(shortage) ? (
            <Button
              status="primary"
              size="small"
              style={styles.button}
              onPress={() => onReceive(shortage)}
              testID="stock-shortage-receive"
            >
              {t('newSale.stockShortageReceive')}
            </Button>
          ) : null}
        </View>
      ))}
      <Text appearance="hint" style={styles.hint} testID={offersReceiving ? 'stock-shortage-receive-hint' : 'stock-shortage-ask-admin'}>
        {t(offersReceiving ? 'newSale.stockShortageReceiveHint' : 'newSale.stockShortageAskAdmin')}
      </Text>
      <Button appearance="ghost" style={styles.button} onPress={onClose} testID="stock-shortage-close">
        {t('newSale.stockShortageBackToCart')}
      </Button>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modal: {
    width: '85%',
    maxWidth: 480,
    borderRadius: 20,
    padding: 24,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  title: {
    marginBottom: 12,
  },
  row: {
    marginBottom: 12,
  },
  hint: {
    marginBottom: 8,
  },
  button: {
    marginTop: 4,
  },
})
